import { Link } from "react-router-dom";

import { Card, CardContent, Grid } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const PaymentAgreement = () => {
  return (
    <MDBox py={3}>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={10} lg={8}>
          <Card>
            <CardContent>
              <MDBox mt={4} textAlign="center" mb={2}>
                <MDTypography variant="h4" fontWeight="medium">
                  TOMARKET AUTOMATIC PAYMENT AUTHORIZATION AGREEMENT
                </MDTypography>
              </MDBox>

              <MDBox p={1}>
                <MDTypography variant="h5" fontWeight="medium" gutterBottom>
                  Effective Date: April 2025
                </MDTypography>
                <MDTypography variant="body2" paragraph>
                  This Automatic Payment Authorization Agreement (“Agreement”) is entered into
                  electronically between Find Better Food, Inc., dba ToMarket (“ToMarket”) and the
                  party accepting this Agreement (“Brand”) as of the date the Brand clicks “I Agree”
                  or otherwise provides electronic acceptance. By accepting this Agreement, the
                  Brand authorizes recurring payments in connection with participation on the
                  ToMarket platform.
                </MDTypography>

                {/* 1 */}
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium" gutterBottom>
                    1. AUTHORIZATION FOR AUTOMATIC PAYMENTS
                  </MDTypography>

                  <MDTypography ml={2} variant="body2" paragraph>
                    The Brand authorizes ToMarket to charge the payment method provided—whether a
                    credit card, debit card, or bank account—for any fees owed as outlined in this
                    Agreement. This includes automatic monthly charges and any applicable one-time
                    fees. The Brand also authorizes ToMarket to issue credits or refunds to the same
                    payment method if needed.
                  </MDTypography>
                </MDBox>

                {/* 2 */}
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                    2. PAYMENT DETAILS
                  </MDTypography>

                  <MDTypography ml={2} variant="h6" fontWeight="medium" gutterBottom>
                    2.1 Billing Frequency
                  </MDTypography>
                  <MDTypography ml={2} variant="body2" paragraph>
                    Charges will occur automatically each month on the same calendar day as the
                    Brand’s signup date.
                  </MDTypography>
                  <MDTypography ml={2} variant="h6" fontWeight="medium" gutterBottom>
                    2.2 Billing Amount
                  </MDTypography>
                  <MDTypography ml={2} variant="body2" paragraph>
                    The Brand will be billed under one of the following pricing structures, as
                    confirmed by ToMarket during onboarding or via written communication:
                  </MDTypography>
                  <ol style={{ listStyleType: "lower-roman", paddingLeft: "4rem" }}>
                    <li>
                      <MDTypography variant="body2">
                        A fixed monthly subscription fee, agreed upon by the Brand and ToMarket.
                        This amount may change from time to time with at least 10 days’ advance
                        notice.
                      </MDTypography>
                    </li>
                    <li>
                      <MDTypography variant="body2">
                        A monthly platform fee plus a variable monthly “Data Access Fee” based on
                        the number of buyers with whom the Brand shares data or interacts on the
                        platform.
                      </MDTypography>
                    </li>
                  </ol>

                  <MDTypography ml={2} variant="body2" paragraph>
                    All charges will be detailed in the Brand’s monthly invoice or confirmation
                    email.
                  </MDTypography>
                </MDBox>

                {/* 3 */}
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                    3. PAYMENT METHODS
                  </MDTypography>
                  <MDTypography mt={2} ml={2} variant="h6" fontWeight="medium" gutterBottom>
                    Payments may be made via:
                  </MDTypography>
                  <ol style={{ listStyleType: "lower-roman", paddingLeft: "4rem" }}>
                    <li>
                      <MDTypography variant="body2">
                        Credit or Debit Card (Visa, MasterCard, Discover, American Express)
                      </MDTypography>
                    </li>
                    <li>
                      <MDTypography variant="body2">Bank Account (ACH withdrawal)</MDTypography>
                    </li>
                  </ol>
                  <MDTypography ml={2} variant="body2" paragraph>
                    All payment details are securely processed through Stripe, a PCI-DSS Level 1
                    certified payment processor. By submitting payment information, the Brand agrees
                    to the Stripe Services Agreement
                    <Link to="https://stripe.com/legal">(https://stripe.com/legal)</Link> and Stripe
                    Privacy Policy{" "}
                    <Link to="https://stripe.com/privacy">(https://stripe.com/privacy)</Link>.
                  </MDTypography>

                  <MDTypography ml={2} variant="body2" paragraph>
                    Charges will appear on your statement as “TOMARKET” or similar.
                  </MDTypography>
                </MDBox>

                {/* 4 */}
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                    4. NOTIFICATIONS & CHANGES
                  </MDTypography>

                  <MDTypography ml={2} variant="h6" fontWeight="medium" gutterBottom>
                    4.1 Confirmation
                  </MDTypography>
                  <MDTypography ml={2} variant="body2" paragraph>
                    A confirmation email will be sent after enrollment and with each monthly charge.
                  </MDTypography>
                  <MDTypography ml={2} variant="h6" fontWeight="medium" gutterBottom>
                    4.2 Notice of Changes:
                  </MDTypography>
                  <MDTypography ml={2} variant="body2" paragraph>
                    ToMarket will notify the Brand at least 10 days before any changes to billing
                    terms, unless those changes are part of a usage-based or previously agreed
                    pricing model.
                  </MDTypography>
                  <MDTypography ml={2} variant="h6" fontWeight="medium" gutterBottom>
                    4.3 Stopping a Payment
                  </MDTypography>
                  <MDTypography ml={2} variant="body2" paragraph>
                    To stop a scheduled payment, the Brand must email ToMarket at least 3 business
                    days in advance.
                  </MDTypography>
                  <MDTypography ml={2} variant="h6" fontWeight="medium" gutterBottom>
                    4.4 Cancellation of Authorization
                  </MDTypography>
                  <MDTypography ml={2} variant="body2" paragraph>
                    This Agreement will remain in effect until canceled by the Brand. To cancel, the
                    Brand must provide at least 15 days’ written notice before the next billing
                    date. Canceling this authorization does not waive any outstanding payment
                    obligations.
                  </MDTypography>
                  <MDTypography ml={2} variant="h6" fontWeight="medium" gutterBottom>
                    4.5 Updates to Agreement
                  </MDTypography>
                  <MDTypography ml={2} variant="body2" paragraph>
                    ToMarket may update the terms of this Agreement from time to time. Any material
                    changes will be communicated to the Brand at least 10 days in advance via email
                    or other electronic notice. Continued use of the ToMarket platform after the
                    effective date of such changes will constitute acceptance of the updated
                    Agreement. If the Brand does not agree to the changes, the Brand may cancel this
                    authorization with 15 days’ written notice.
                  </MDTypography>
                </MDBox>

                {/* 5 */}
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                    5. SECURITY & PRIVACY
                  </MDTypography>

                  <MDTypography ml={2} variant="body2" paragraph>
                    ToMarket does not store full payment information. All payments are securely
                    processed through Stripe. Data is encrypted and only accessible by authorized
                    personnel. For more details, see ToMarket’s Privacy Policy
                    <Link to="https://tomarket.com/privacy">(https://tomarket.com/privacy)</Link>.
                  </MDTypography>
                </MDBox>

                {/* 6 */}
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                    6. ACCEPTANCE & CONSENT
                  </MDTypography>

                  <MDTypography ml={2} variant="body2" paragraph>
                    By clicking “I Agree” or otherwise accepting this Agreement electronically:
                  </MDTypography>
                  <ol style={{ listStyleType: "lower-roman", paddingLeft: "4rem" }}>
                    <li>
                      <MDTypography variant="body2">
                        You authorize ToMarket to charge your selected payment method as described
                        above.
                      </MDTypography>
                    </li>
                    <li>
                      <MDTypography variant="body2">
                        You confirm that you have reviewed and understand this Agreement.
                      </MDTypography>
                    </li>
                    <li>
                      <MDTypography variant="body2">
                        You consent to use of electronic records and signatures in accordance with
                        the U.S. Electronic Signatures in Global and National Commerce (E-SIGN) Act.
                      </MDTypography>
                    </li>
                    <li>
                      <MDTypography variant="body2">
                        You understand that this authorization will continue until canceled and that
                        charges may recur monthly based on your selected pricing plan and platform
                        usage.
                      </MDTypography>
                    </li>
                  </ol>

                  <MDTypography ml={2} variant="body2" paragraph>
                    A copy of this Agreement will be sent to your email address and is available
                    upon request.
                  </MDTypography>
                </MDBox>

                {/* 7*/}
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                    7. Support
                  </MDTypography>

                  <MDTypography ml={2} variant="body2" paragraph>
                    If you have any questions or concerns about this Agreement, please email us at{" "}
                    <strong>
                      <Link
                        to="mailto:info@tomarket.com"
                        style={{ fontWeight: "bold", textDecoration: "none", color: "#1976d2" }}
                      >
                        info@tomarket.com
                      </Link>
                    </strong>{" "}
                    .
                  </MDTypography>
                </MDBox>
              </MDBox>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default PaymentAgreement;
